import { useNotificationStore } from '@/stores/notification';
import { useI18n } from 'vue-i18n';

/** Only these operations have safe, well-formed translation keys (no ICU from backend). */
const API_SUCCESS_OPERATIONS = ['created', 'updated', 'deleted', 'completed'] as const;

export function useNotification() {
  const store = useNotificationStore();
  const { t } = useI18n();

  const showSuccess = (message: string, options?: Parameters<typeof store.showSuccess>[1]) => {
    return store.showSuccess(message, options);
  };

  const showError = (message: string, options?: Parameters<typeof store.showError>[1]) => {
    return store.showError(message, options);
  };

  const showWarning = (message: string, options?: Parameters<typeof store.showWarning>[1]) => {
    return store.showWarning(message, options);
  };

  const showInfo = (message: string, options?: Parameters<typeof store.showInfo>[1]) => {
    return store.showInfo(message, options);
  };

  const showApiSuccess = (operation: string, entity?: string) => {
    const safeOp =
      API_SUCCESS_OPERATIONS.indexOf(operation as (typeof API_SUCCESS_OPERATIONS)[number]) >= 0
        ? operation
        : 'completed';
    try {
      const message = t(`api.success.${safeOp}`, { entity: entity || 'item' });
      return showSuccess(message);
    } catch {
      return showSuccess('Operation completed successfully.');
    }
  };

  /** Only translate known api.* keys; never pass backend/user text to t() to avoid ICU SyntaxError. */
  const safeT = (key: string, fallback: string): string => {
    if (!/^api\.(success|errors)\.[a-zA-Z]+$/.test(key)) return fallback;
    try {
      const result = t(key);
      return typeof result === 'string' ? result : fallback;
    } catch {
      return fallback;
    }
  };

  const showApiError = (error: unknown, context?: string) => {
    let message: string;

    if (typeof error === 'string') {
      message = error;
    } else if (error && typeof error === 'object') {
      const apiError = error as Record<string, unknown>;
      const response = apiError.response as Record<string, unknown> | undefined;
      const data = response?.data as Record<string, unknown> | undefined;

      // Network/SSL error: no response (request failed before server replied)
      if (response === undefined && apiError.request) {
        message = safeT(
          'api.errors.networkError',
          'Network connection error. Please check your connection and try again.'
        );
      } else if (data?.message) {
        message = data.message as string;
      } else if (data?.error) {
        message = data.error as string;
      } else {
        message = safeT('api.errors.generic', 'An unexpected error occurred. Please try again.');
      }
    } else {
      message = safeT('api.errors.generic', 'An unexpected error occurred. Please try again.');
    }

    if (context) {
      message = `${context}: ${message}`;
    }

    return showError(message);
  };

  return {
    // Notification methods
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showApiSuccess,
    showApiError,

    // Store methods
    removeNotification: store.removeNotification,
    clearAllNotifications: store.clearAllNotifications,
  };
}
